import React, { useState, useMemo } from 'react';
import {
  ComposedChart, Bar, Line, XAxis, YAxis, CartesianGrid, Tooltip as RTooltip,
  ResponsiveContainer, Legend, ReferenceLine,
} from 'recharts';
import { Users, ArrowUpRight } from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle } from '../ui/card';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../ui/select';
import { Switch } from '../ui/switch';

const COMPONENTS = [
  { key: 'capability', name: 'Capability', color: '#6366f1' },
  { key: 'motivation', name: 'Motivation', color: '#f59e0b' },
  { key: 'retention', name: 'Retention', color: '#10b981' },
];

function periodLabel(p) {
  if (!p || p.length < 7) return 'last month';
  const m = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];
  try { const [y, mm] = p.split('-'); return `${m[Number(mm) - 1]} ${y.slice(2)}`; } catch { return 'last month'; }
}
const pct = (v) => Math.round((v || 0) * 100);
const peText = (v) => (v >= 0.75 ? 'text-emerald-500' : v >= 0.6 ? 'text-amber-500' : 'text-rose-500');

export default function PeCohortComparison({ cohortScores = [], cohortScoresPrev = [], prevPeriod, isDark = false, onCohortClick, onCohortTrends, testId = 'pe-cohort-comparison' }) {
  const [selected, setSelected] = useState('all');
  const [showPrior, setShowPrior] = useState(true);
  const lineColor = isDark ? '#e2e8f0' : '#0f172a';
  const gridColor = isDark ? '#1e293b' : '#f1f5f9';
  const axisColor = isDark ? '#334155' : '#e2e8f0';
  const tickColor = isDark ? '#94a3b8' : '#64748b';
  const tableBorder = isDark ? 'border-white/5' : 'border-slate-100';

  const prevBy = useMemo(() => {
    const m = {};
    cohortScoresPrev.forEach((c) => { m[c.cohort_id] = c; });
    return m;
  }, [cohortScoresPrev]);

  const hasPrior = cohortScoresPrev.length > 0;
  const prevLbl = periodLabel(prevPeriod);

  const rows = useMemo(() => {
    const src = selected === 'all' ? cohortScores : cohortScores.filter((c) => c.cohort_id === selected);
    return src.map((c) => {
      const p = prevBy[c.cohort_id];
      return {
        cohort_id: c.cohort_id,
        cohort_name: c.cohort_name,
        capability: Math.round((c.capability || 0) * 100),
        motivation: Math.round((c.motivation || 0) * 100),
        retention: Math.round((c.retention || 0) * 100),
        pe: Math.round((c.pe || 0) * 100),
        capability_prev: p ? Math.round((p.capability || 0) * 100) : null,
        motivation_prev: p ? Math.round((p.motivation || 0) * 100) : null,
        retention_prev: p ? Math.round((p.retention || 0) * 100) : null,
      };
    });
  }, [cohortScores, prevBy, selected]);

  const avgPe = useMemo(() => {
    const src = selected === 'all' ? cohortScores : cohortScores.filter((c) => c.cohort_id === selected);
    if (!src.length) return null;
    return Math.round((src.reduce((s, c) => s + (c.pe || 0), 0) / src.length) * 100);
  }, [cohortScores, selected]);

  if (!cohortScores.length) return null;

  return (
    <Card data-testid={testId}>
      <CardHeader className="pb-2">
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
          <div>
            <CardTitle className="flex items-center gap-2 text-base">
              <Users className="h-5 w-5 text-indigo-600" /> Cohort PE Comparison
            </CardTitle>
            <p className="text-xs text-slate-500">Capability, Motivation &amp; Retention per cohort (0–100) with each cohort's composite PE as a dashed line. Faint bars show {prevLbl}.</p>
          </div>
          <div className="flex items-center gap-3">
            {hasPrior && (
              <label className="flex items-center gap-1.5 text-xs font-semibold text-slate-600 cursor-pointer">
                <Switch checked={showPrior} onCheckedChange={setShowPrior} data-testid="pe-cohort-compare-prior-toggle" />
                vs {prevLbl}
              </label>
            )}
            <Select value={selected} onValueChange={setSelected}>
              <SelectTrigger className="w-52" data-testid="pe-cohort-compare-filter"><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All cohorts</SelectItem>
                {cohortScores.map((c) => <SelectItem key={c.cohort_id} value={c.cohort_id}>{c.cohort_name}</SelectItem>)}
              </SelectContent>
            </Select>
          </div>
        </div>
      </CardHeader>
      <CardContent>
        <ResponsiveContainer width="100%" height={320}>
          <ComposedChart data={rows} margin={{ top: 16, right: 72, left: -8, bottom: 60 }} barGap={2} barCategoryGap="18%">
            <CartesianGrid strokeDasharray="3 3" stroke={gridColor} />
            <XAxis dataKey="cohort_name" tick={{ fontSize: 10, angle: -30, textAnchor: 'end', fill: tickColor }} interval={0} height={70} stroke={axisColor} />
            <YAxis domain={[0, 100]} tickFormatter={(v) => `${v}%`} tick={{ fontSize: 11, fill: tickColor }} stroke={axisColor} />
            <RTooltip contentStyle={{ background: isDark ? '#0f172a' : '#fff', border: `1px solid ${axisColor}`, borderRadius: 8, color: isDark ? '#e2e8f0' : '#0f172a' }} formatter={(v, n) => [v == null ? '—' : `${v}%`, n]} />
            <Legend wrapperStyle={{ fontSize: 11, paddingTop: 8, color: tickColor }} />
            {avgPe != null && (
              <ReferenceLine y={avgPe} stroke={lineColor} strokeDasharray="6 4" strokeOpacity={0.7}
                label={{ value: `Avg PE ${avgPe}%`, position: 'insideTopRight', fill: lineColor, fontSize: 10, fontWeight: 700 }} />
            )}
            {COMPONENTS.map((c) => (
              <Bar key={c.key} dataKey={c.key} name={c.name} fill={c.color} radius={[3, 3, 0, 0]} maxBarSize={26}
                cursor={onCohortClick ? 'pointer' : 'default'}
                onClick={(d) => onCohortClick && d?.payload?.cohort_id && onCohortClick(d.payload.cohort_id)} />
            ))}
            {hasPrior && showPrior && COMPONENTS.map((c) => (
              <Bar key={`${c.key}_prev`} dataKey={`${c.key}_prev`} name={`${c.name} (${prevLbl})`} fill={c.color} fillOpacity={0.28} radius={[3, 3, 0, 0]} maxBarSize={26} />
            ))}
            <Line type="monotone" dataKey="pe" name="Composite PE" stroke={lineColor} strokeWidth={2} strokeDasharray="6 4" dot={{ r: 3, fill: lineColor }} activeDot={{ r: 5 }} />
          </ComposedChart>
        </ResponsiveContainer>
        {onCohortClick && <p className="text-[10px] text-slate-400 mt-1">Tip: click a cohort's bar to drill into its component breakdown.</p>}

        {/* Cohort detail table */}
        <div className="mt-5 overflow-x-auto">
          <table className="w-full text-sm">
            <thead>
              <tr className={`border-b ${tableBorder}`}>
                {['Cohort', 'PE', 'Cap (C)', 'Mot (M)', 'Ret (R)', 'LEI', 'PPE'].map((hh, i) => (
                  <th key={hh} className={`py-2 px-3 font-medium ${i === 0 ? 'text-left' : 'text-center'} ${isDark ? 'text-slate-400' : 'text-slate-500'}`}>{hh}</th>
                ))}
                {onCohortTrends && <th className="py-2 px-3" />}
              </tr>
            </thead>
            <tbody>
              {(selected === 'all' ? cohortScores : cohortScores.filter((c) => c.cohort_id === selected)).map((cs) => (
                <tr key={cs.cohort_id}
                  onClick={() => onCohortClick && onCohortClick(cs.cohort_id)}
                  className={`border-b ${tableBorder} ${onCohortClick ? 'cursor-pointer' : ''} ${isDark ? 'hover:bg-white/5' : 'hover:bg-slate-50'}`}
                  data-testid={`pe-cohort-row-${cs.cohort_id}`}>
                  <td className={`py-2 px-3 font-medium ${isDark ? 'text-slate-200' : 'text-slate-700'}`}>{cs.cohort_name}</td>
                  <td className={`py-2 px-3 text-center font-bold ${peText(cs.pe)}`}>{pct(cs.pe)}%</td>
                  <td className={`py-2 px-3 text-center ${isDark ? 'text-slate-300' : 'text-slate-600'}`}>{pct(cs.capability)}%</td>
                  <td className={`py-2 px-3 text-center ${isDark ? 'text-slate-300' : 'text-slate-600'}`}>{pct(cs.motivation)}%</td>
                  <td className={`py-2 px-3 text-center ${isDark ? 'text-slate-300' : 'text-slate-600'}`}>{pct(cs.retention)}%</td>
                  <td className={`py-2 px-3 text-center ${isDark ? 'text-slate-300' : 'text-slate-600'}`}>{pct(cs.lei)}%</td>
                  <td className={`py-2 px-3 text-center ${isDark ? 'text-slate-300' : 'text-slate-600'}`}>{pct(cs.ppe)}%</td>
                  {onCohortTrends && (
                    <td className="py-2 px-3 text-right">
                      <button onClick={(e) => { e.stopPropagation(); onCohortTrends(cs.cohort_id); }}
                        data-testid={`pe-cohort-trends-${cs.cohort_id}`}
                        className="inline-flex items-center gap-1 text-[11px] font-semibold text-[#44b6d2] hover:text-cyan-500 transition-colors">
                        Trends <ArrowUpRight className="h-3 w-3" />
                      </button>
                    </td>
                  )}
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </CardContent>
    </Card>
  );
}
